import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '../../utils/notificationUtils';

const SpecialRequestsNoteModal = ({
  isOpen,
  onClose,
  value = '',
  onSave,
  onPersist,
  title,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setNote(value || '');
  }, [isOpen, value]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const next = note;
    if (onSave) onSave(next);

    if (!onPersist) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await onPersist(next);
      showSuccessToast(t('receptionInpatients.specialRequests.saved', { defaultValue: 'Special requests saved.' }));
      onClose();
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message ||
        t('receptionInpatients.specialRequests.saveFailed', { defaultValue: 'Failed to save special requests.' })
      );
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(6px)',
        zIndex: 10050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div style={{ fontWeight: 800, color: '#0f172a' }}>
            {title || t('receptionInpatients.specialRequests.title', { defaultValue: 'Special Requests' })}
          </div>
          <button
            type="button"
            onClick={() => {
              if (!saving) onClose();
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
            {t('receptionInpatients.specialRequests.noteLabel', { defaultValue: 'Notes' })}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('receptionInpatients.specialRequests.placeholder', { defaultValue: 'Write special requests here...' })}
            disabled={saving}
            style={{
              width: '100%',
              minHeight: 200,
              resize: 'vertical',
              padding: 14,
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              color: '#0f172a',
              outline: 'none',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#fff',
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (!saving) onClose();
            }}
            disabled={saving}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              color: '#334155',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 16px',
              borderRadius: 12,
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: '#fff',
              fontWeight: 800,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'} />
              {saving ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default SpecialRequestsNoteModal;

